import { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  useMediaQuery,
  Divider,
  ListItemText,
  Select,
  FormControl,
  Tooltip,
} from '@mui/material';
import { useDialogContext } from '../contexts/DialogContext';
import { useOktaAuthContext } from '../contexts/OktaAuthContext.tsx';
import icon from '/logo-engie-small-blue.svg';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import AppsIcon from '@mui/icons-material/Apps';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext, Apps, Roles } from '../contexts/AppContext';
import app_icon from '/logo-engie-small-blue.svg';
import About from '../components/dialogs/About';
import logo_engie from '../assets/logo_engie.png';
import logo_engie_white from '../assets/logo_engie_white.png';
import logo_lbe from '../assets/logo_lbe.png';
import logo_lbe_white from '../assets/logo_lbe_white.png';

const AppHeader = () => {
  const { app, role, setNewRole, isDarkMode, setIsDarkMode } = useAppContext();
  const { openDialog } = useDialogContext();
  const { logout } = useOktaAuthContext();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const navigate = useNavigate();
  const location = useLocation();

  const settings = [
    {
      name: 'Soiling Mitigation',
      icon: <img src={isSmallScreen ? icon : app_icon} height={'15px'} />,
      function: () => {
        navigate('/');
      },
      isSelected: () => app === Apps.SoilingMitigation,
    },
    {
      name: 'Logout',
      icon: <LogoutOutlinedIcon fontSize="small" />,
      function: () => {
        logout();
      },
      divider: true,
      isSelected: () => false,
    },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const toggleDarkMode = () => {
    const newIsDarkMode = !isDarkMode;
    setIsDarkMode(newIsDarkMode);
    localStorage.setItem('isDarkTheme', JSON.stringify(newIsDarkMode));
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar>
        {app === Apps.SoilingMitigation && (
          <>
            <Box sx={{ display: { xs: 'flex', md: 'flex' }, mr: 2 }} component="a" href="/">
              {isSmallScreen ? (
                <img src={icon} height={'40px'} />
              ) : (
                <>
                  <img src={isDarkMode ? logo_engie_white : logo_engie} height={'40px'} />{' '}
                  <img src={isDarkMode ? logo_lbe_white : logo_lbe} height={'40px'} />
                </>
              )}
            </Box>
            <Typography
              variant="h5"
              noWrap
              color="text.primary"
              sx={{
                display: 'flex',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              {Apps.SoilingMitigation}
            </Typography>
          </>
        )}
        {app === Apps.UserManager && (
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              color: 'rgba(0, 0, 0, 0.54)',
              textDecoration: 'none',
            }}
          >
            User Manager
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center' }}></Box>
        <Box>
          {location.pathname !== '/projects' && (
            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <Select label="Role" name={'role'} value={role} onChange={(e) => setNewRole(e.target.value)}>
                {Roles.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Tooltip title={isDarkMode ? 'Activate light mode' : 'Activate dark mode'}>
          <IconButton size="large" onClick={toggleDarkMode}>
            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={'About'}>
          <IconButton
            size="large"
            onClick={() =>
              openDialog(
                {
                  text: 'About',
                },
                <About />,
              )
            }
          >
            <HelpOutlineOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={'Menu'}>
          <IconButton size="large" edge="end" onClick={handleOpenUserMenu}>
            <AppsIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleCloseUserMenu}>
          {settings.map((setting) => [
            setting.divider && <Divider />,
            <MenuItem
              key={setting.name}
              onClick={() => {
                handleCloseUserMenu();
                setting.function();
              }}
              selected={setting.isSelected()}
            >
              <ListItemIcon>{setting.icon}</ListItemIcon>
              <ListItemText>{setting.name}</ListItemText>
            </MenuItem>,
          ])}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
